import { BankTransferInstructions } from "@/components/payment/BankTransferInstructions";
import { SupplierCollectedInstructions } from "@/components/payment/SupplierCollectedInstructions";
import { Button } from "@/components/ui/button";
import { isBankTransferUnpayable } from "@/lib/bank-transfer";
import { SUPPORT_EMAIL } from "@/lib/support";
import {
  isBankTransfer,
  isManual,
  isStripe,
  paymentInfoMap
} from "@/lib/payment";
import {
  isSupplierCollectedCheckout,
  SUPPLIER_COLLECTED_PROVIDER_ID
} from "@/lib/supplier-collected";
import { useBillingAddressChange } from "@/pages/checkout/hooks";
import { useUpdateCartsAddresses } from "@/queries/cart/mutations";
import { useSellerCart } from "@/queries/cart/queries";
import { queryKeys } from "@/queries/cart/queryKeys";
import {
  currentPaymentSessionIds,
  useCompletePayment,
  usePaymentProviders
} from "@/queries/payment";
import { collapseProvenance } from "@/utils/cart";
import { toMinorUnits } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import { Form } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, CreditCard, Loader2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BillingAddressSection } from "../components/BillingAddressSection";
import { CheckoutActionBar } from "../components/CheckoutActionBar";
import {
  StripePaymentForm,
  type StripePaymentFormRef
} from "../components/StripePaymentForm";
import { useCheckoutAddressState } from "../hooks/useCheckoutAddressState";
import { usePaymentSessions } from "../hooks/usePaymentSessions";
import {
  PaymentFailedError,
  type PaymentInstrument,
  type SelectedGateway
} from "../payment/gateway";
import { analytics } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import {
  PaymentPlanRefusedError,
  payForCarts,
  type CheckoutOps
} from "../payment/pay-for-carts";
import { resolveCheckoutAddresses } from "../resolve-addresses";
import { gatewayFor } from "../payment/stripe-gateway";
import type { CheckoutAddressFormData } from "../validations";
import type { FieldErrors } from "react-hook-form";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

/**
 * The form fields the Delivery step owns. This step re-validates the whole
 * address form on submit but renders none of these, so an error on one of them
 * has to be reported here and sent back to where it can be fixed - left to
 * `formState.errors`, it stopped the submit with nothing on screen (KAN-230).
 */
const DELIVERY_FIELDS = [
  "email",
  "phone",
  "shippingAddressId",
  "shippingAddress"
] as const satisfies readonly (keyof CheckoutAddressFormData)[];

/**
 * The text to show for a payment step failure, reporting it when unexpected. A
 * decline or refused plan carries words meant for the buyer and is shown as-is,
 * unreported; anything else goes through the shared rule (a deliberate 400/409/
 * 422 is shown, the rest is reported and replaced by `fallback`).
 */
function paymentErrorMessage(
  error: unknown,
  options: { boundary: string; fallback: string }
): string {
  if (
    error instanceof PaymentFailedError ||
    error instanceof PaymentPlanRefusedError
  ) {
    return error.message;
  }
  return handleActionError(error, options);
}

/**
 * Why the order cannot be placed from this step, with the way back to the
 * Delivery step that can fix it.
 */
const DeliveryIssueAlert = memo(function DeliveryIssueAlert({
  message,
  cartIds
}: {
  message: string;
  cartIds: string[];
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      role="alert"
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
      <Link
        to="/checkout"
        search={{ cart_ids: cartIds.join(","), step: "information" }}
        className="ml-auto inline-flex items-center gap-1 rounded font-medium text-red-700 underline underline-offset-2 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to Delivery
      </Link>
    </div>
  );
});

export interface PaymentStepProps {
  /** Cart IDs being checked out */
  cartIds: string[];
  /** Carts being checked out (for shipping method section) */
  carts: StoreSellerCart[];
}

export const PaymentStep = memo(function PaymentStep({
  cartIds,
  carts
}: PaymentStepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A supplier-collected checkout (KAN-127): these suppliers bill the salon
  // directly, so the payment step forces a dedicated no-capture provider,
  // suppresses Stripe entirely, and shows an informational panel in place of the
  // card form. The set is homogeneous - the cart page blocks a mode-mixed
  // "Checkout all" and the checkout page refuses a mixed URL - so this one flag
  // answers for the whole checkout. Read off the carts (not a payment provider),
  // because the dedicated provider is deliberately not region-enabled and so
  // never appears in the region providers list.
  const isSupplierCollected = isSupplierCollectedCheckout(carts);
  const supplierCollectedSellerNames = useMemo(
    () => Array.from(new Set(carts.map((cart) => cart.seller.name))),
    [carts]
  );

  const [submitError, setSubmitError] = useState<string | null>(null);
  // A submit blocked by a field only the Delivery step shows - see
  // DELIVERY_FIELDS.
  const [deliveryIssue, setDeliveryIssue] = useState<string | null>(null);

  // A cart can lose its method between steps - the server drops one the
  // address no longer offers (api KAN-121) - and this step has no picker to
  // choose another, so it says so up front and holds the submit, rather than
  // showing "Not selected" beside an enabled button (KAN-230).
  const missingShippingMessage = useMemo(() => {
    const sellerNames = carts
      .filter((cart) => cart.shipping_methods.length === 0)
      .map((cart) => cart.seller.name);
    if (sellerNames.length === 0) return null;
    return carts.length > 1
      ? `Choose a shipping method for ${sellerNames.join(", ")} first.`
      : "Choose a shipping method first.";
  }, [carts]);

  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    // Force the dedicated provider from the first render for a supplier-collected
    // checkout, so no Stripe session is ever initiated for it. For a marketplace
    // checkout it starts unselected and is chosen from the region providers.
    //
    // The initializer runs only at mount, which is sound because `carts` is
    // known then and does not change mode under this component: `CheckoutPage`
    // renders `EmptyCheckoutState` rather than mounting this step with no carts,
    // and the selection that feeds `carts` is fixed by the URL for the life of
    // the step. If that ever changed - carts arriving or switching mode after
    // mount - this would need to become an effect keyed on `isSupplierCollected`.
    isSupplierCollected ? SUPPLIER_COLLECTED_PROVIDER_ID : null
  );
  const completePayment = useCompletePayment();
  const firstCart = cartIds[0];

  // Region and currency come off the cart list this step was handed, not off a
  // cart retrieve. Both are region-level and identical across a customer's
  // carts, so the first one answers for the whole checkout - and reading them
  // here means the providers query below fires on mount instead of queueing
  // behind a full-cart round trip.
  //
  // It also retires a hardcoded "aud" fallback. The marketplace is
  // single-currency and stays that way, so that literal was not wrong; it was
  // just a second place stating a fact the cart already carries, free to
  // disagree with the money it formats and answering for a cart it never read.
  //
  // Optional-chained for the empty-carts case only. `CheckoutPage` renders
  // `EmptyCheckoutState` instead of this step when there are none, so the
  // chain never actually short-circuits.
  const regionId = carts[0]?.region_id;
  const currencyCode = carts[0]?.currency_code;

  // Still retrieved, but no longer in front of anything: the address state
  // needs the cart's shipping/billing addresses to pre-select against, and the
  // list payload carries no addresses.
  const { data: cartData, isLoading: cartLoading } = useSellerCart(firstCart, {
    enabled:
      !!firstCart && !(completePayment.isSuccess || completePayment.isPending)
  });

  const { form, addresses, isLoading } = useCheckoutAddressState({
    //
    cartData,
    cartLoading
  });

  const updateAddressesMutation = useUpdateCartsAddresses();

  const { handleSubmit, formState } = form;
  const { isSubmitting } = formState;

  const handleBillingAddressChange = useBillingAddressChange({
    cartIds,
    updateAddressesMutation
  });

  // Fetch available payment providers for this region
  const { data: providersData, isLoading: isLoadingProviders } =
    usePaymentProviders(regionId);
  const paymentProviders = providersData?.payment_providers ?? [];

  // Payment session management
  const {
    sessions,
    allReady,
    isInitializing,
    initError,
    initializeSessions,
    updateSessionStatus,
    reset: resetSessions
  } = usePaymentSessions();

  // Refs for each cart's Stripe form (holds the live Elements context).
  const paymentFormRefs = useRef<Map<string, StripePaymentFormRef>>(new Map());
  // Gateway + instruments prepared in handlePayClick, consumed in onSubmit.
  const gatewayRef = useRef<SelectedGateway | null>(null);
  const instrumentsRef = useRef<PaymentInstrument[]>([]);

  // Auto-select the only provider if there's just one. Never for a
  // supplier-collected checkout: its provider is already forced, and letting a
  // region provider (Stripe) win here - even for a frame - could mint a real
  // PaymentIntent for a supplier who bills the salon directly.
  useEffect(() => {
    if (isSupplierCollected) return;
    if (paymentProviders.length === 1 && !selectedProviderId) {
      setSelectedProviderId(paymentProviders[0].id);
    }
  }, [isSupplierCollected, paymentProviders, selectedProviderId]);

  // Start - or restart - initialisation for the currently selected provider.
  //
  // One callback, two callers: the effect just below, and the retry control on
  // the init-error alert. They want the same thing under the same conditions,
  // and were stating that predicate twice. `usePaymentSessions` owns the
  // re-entry guard (it latches synchronously, before its first `await`), so
  // calling this while a run is already going is a no-op rather than a race.
  const startInitialization = useCallback(() => {
    if (!selectedProviderId || carts.length === 0) return;
    void initializeSessions(carts, selectedProviderId);
  }, [carts, selectedProviderId, initializeSessions]);

  // Initialize payment sessions when a provider is selected
  useEffect(() => {
    startInitialization();
  }, [startInitialization]);

  // Handle provider selection change
  const handleProviderChange = useCallback(
    (providerId: string) => {
      if (providerId === selectedProviderId) return;
      resetSessions();
      paymentFormRefs.current.clear();
      setSelectedProviderId(providerId);
    },
    [selectedProviderId, resetSessions]
  );

  // The carts whose `order_confirmed` has already been sent. A retry after a
  // partial failure re-runs `payForCarts` over every cart, and completing one that
  // already is an order hands back the same order, so without this the retry
  // would report the earlier carts a second time.
  const reportedCartIdsRef = useRef<Set<string>>(new Set());

  // The pilot's conversion event, for the carts in `completedCartIds` that have
  // not been reported yet. Scoped to this **internal** path on purpose: an
  // external handoff is already covered by `external_checkout_confirm_clicked`
  // (client) and `external_checkout_redirected` (server), and one event spanning
  // both would double-count the external path and conflate "ordered" with
  // "handed off to the supplier".
  //
  // Counts and total cover only the carts being reported. `partial` marks a
  // checkout where some other cart is still not an order, so a multi-seller
  // checkout that fails halfway reports what it really sold instead of nothing.
  const reportCompletedCarts = useCallback(
    (completedCartIds: ReadonlySet<string>) => {
      const reported = reportedCartIdsRef.current;
      const fresh = carts.filter(
        (cart) =>
          completedCartIds.has(cart.cart_id) && !reported.has(cart.cart_id)
      );
      if (fresh.length === 0) return;
      for (const cart of fresh) reported.add(cart.cart_id);

      const failedCartCount = carts.filter(
        (cart) => !reported.has(cart.cart_id)
      ).length;
      analytics.orderConfirmed({
        cart_count: fresh.length,
        item_count: fresh.reduce(
          (sum, cart) =>
            sum + cart.items.reduce((n, item) => n + item.quantity, 0),
          0
        ),
        total: fresh.reduce((sum, cart) => sum + cart.totals.total, 0),
        currency_code: currencyCode,
        failed_cart_count: failedCartCount,
        partial: failedCartCount > 0
      });
    },
    [carts, currencyCode]
  );

  // Handle form submission
  const onSubmit = useCallback(
    async (data: CheckoutAddressFormData) => {
      setSubmitError(null);

      if (!selectedProviderId) {
        setSubmitError("Please select a payment method");
        return;
      }

      // Backstop for the disabled button: the missing-method alert is already
      // on screen with its way back to Delivery.
      if (missingShippingMessage) {
        return;
      }

      // Saved-id / inline / same-as-shipping, for both addresses. Resolved by
      // `resolve-addresses`, which returns the failure rather than reporting
      // it, so the branching is exercisable without standing this step up.
      const resolved = resolveCheckoutAddresses(data, addresses);
      if (!resolved.ok) {
        setSubmitError(resolved.error);
        return;
      }
      const { shipping: shippingAddressData, billing: billingAddressData } =
        resolved;

      // The cart/order side-effects payForCarts drives, pre-bound to the
      // resolved addresses and this checkout's mutations.
      const ops: CheckoutOps = {
        updateAddresses: async () => {
          await updateAddressesMutation.mutateAsync({
            cartIds,
            email: data.email,
            shippingAddress: shippingAddressData,
            billingAddress: billingAddressData
          });
        },
        currentPaymentSessionIds,
        createOrderSet: async () => {
          const { order_set } = await sdk.store.orderSet.create();
          return order_set.id;
        },
        completeCart: async (cartId, paymentSessionId, orderSetId) => {
          const result = await completePayment.mutateAsync({
            cartId,
            paymentSessionId,
            orderSetId
          });
          if (result.type === "order" && result.order) {
            return result.order.id;
          }
          const sellerName =
            sessions.find((s) => s.cartId === cartId)?.sellerName ?? cartId;
          throw new Error(`Failed to complete order for ${sellerName}`);
        }
      };

      // The gateway + instruments were prepared in handlePayClick (Stripe
      // requires elements.submit() + createPaymentMethod to run synchronously
      // from the click, before any async work).
      if (!gatewayRef.current) {
        setSubmitError("Payment not ready. Please try again.");
        return;
      }

      // The carts this attempt turned into orders. `payForCarts` is fail-fast, so
      // when a later seller's payment or completion throws, the carts before it
      // are already real orders - and its return value, which would have named
      // them, never arrives. This is how the failure path still knows.
      const completedCartIds = new Set<string>();

      try {
        const { orderIds } = await payForCarts({
          sessions,
          instruments: instrumentsRef.current,
          // Gateway + the provider it was built for, as one value: payForCarts
          // refuses a session plan belonging to a different provider.
          payment: gatewayRef.current,
          ops,
          onProgress: updateSessionStatus,
          onCartCompleted: (cartId) => completedCartIds.add(cartId)
        });

        reportCompletedCarts(completedCartIds);

        // Refresh the cart list (MainLayout badge) before leaving.
        await queryClient.invalidateQueries({
          queryKey: queryKeys.sellerCarts.all,
          exact: true
        });

        navigate({
          to: "/order-confirmation",
          search: { order_ids: orderIds.join(",") }
        });
      } catch (error) {
        // Orders that were created before the failure are conversions all the
        // same. A no-op when nothing completed (a decline, a refused plan).
        reportCompletedCarts(completedCartIds);

        setSubmitError(
          paymentErrorMessage(error, {
            boundary: "checkout place order",
            fallback: "We couldn't place your order. Please try again."
          })
        );

        // A refused plan is unpayable as it stands but nothing was charged, and
        // a fresh initialisation produces one that can be paid. Re-arm it here,
        // because the buyer cannot: re-selecting the same provider
        // early-returns, the pilot ships one provider so there is no other
        // radio, and the "Try again" control is bound to `initError`. Without
        // this the refusal is a dead checkout - the shape CLAUDE.md's payment
        // note warns about. Deliberately not done for a payment *failure*: a
        // decline leaves confirmed carts confirmed, and wiping the plan there
        // would invite a double charge.
        if (error instanceof PaymentPlanRefusedError) {
          resetSessions();
          startInitialization();
        }
        // Confirmed carts are left confirmed on purpose - resetting them to
        // "ready" would invite a double-charge if the buyer retries.
      }
    },
    [
      addresses,
      cartIds,
      carts,
      completePayment,
      currencyCode,
      missingShippingMessage,
      navigate,
      queryClient,
      reportCompletedCarts,
      resetSessions,
      selectedProviderId,
      sessions,
      startInitialization,
      updateAddressesMutation,
      updateSessionStatus
    ]
  );

  const isPending =
    isSubmitting ||
    updateAddressesMutation.isPending ||
    completePayment.isPending;

  // Marketplace-level config, so any session carrying details carries the same
  // ones; null means the provider sent a partial payload (a misconfiguration).
  const bankTransferDetails =
    sessions.find((s) => s.bankTransfer)?.bankTransfer ?? null;
  const bankTransferUnpayable = isBankTransferUnpayable({
    providerId: selectedProviderId,
    sessionCount: sessions.length,
    details: bankTransferDetails
  });

  const canSubmit =
    allReady &&
    !isPending &&
    !isLoading &&
    !!selectedProviderId &&
    !bankTransferUnpayable &&
    !missingShippingMessage;

  const deliveryAlert = missingShippingMessage ?? deliveryIssue;

  // Validation failed. A field this step renders (billing) shows its own
  // message; one only the Delivery step renders is reported here, or the click
  // would end with nothing on screen.
  const onInvalid = useCallback(
    (errors: FieldErrors<CheckoutAddressFormData>) => {
      const deliveryField = DELIVERY_FIELDS.find((field) => errors[field]);
      if (!deliveryField) {
        setSubmitError(
          errors.billingAddressId || errors.billingAddress
            ? "Please check your billing address."
            : "Please check your details and try again."
        );
        return;
      }
      const message = errors[deliveryField]?.message;
      setDeliveryIssue(
        typeof message === "string" && message
          ? `${message.replace(/\.$/, "")}. Fix it on the Delivery step.`
          : "Your delivery details are incomplete. Fix them on the Delivery step."
      );
    },
    []
  );

  // Stripe requires elements.submit() + createPaymentMethod in the synchronous
  // click handler context, before any other async work. So the gateway prepares
  // all payment instruments upfront in onClick (manual no-ops), then
  // react-hook-form's handleSubmit runs onSubmit.
  const handlePayClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      setSubmitError(null);
      setDeliveryIssue(null);

      if (!selectedProviderId) {
        setSubmitError("Please select a payment method");
        return;
      }

      // The button is already disabled for this; the check is cheap and the
      // consequence of slipping through is an order with no account to pay into.
      if (bankTransferUnpayable) {
        setSubmitError(
          `Bank transfer is unavailable right now. Please choose another payment method, or contact ${SUPPORT_EMAIL}.`
        );
        return;
      }

      let selected: SelectedGateway;
      try {
        selected = gatewayFor(selectedProviderId, {
          stripeRef: paymentFormRefs.current.get(sessions[0]?.cartId)
        });
      } catch (err) {
        setSubmitError(
          paymentErrorMessage(err, {
            boundary: "checkout payment method",
            fallback:
              "This payment method isn't available right now. Please try again."
          })
        );
        return;
      }

      try {
        gatewayRef.current = selected;
        instrumentsRef.current = await selected.gateway.prepare(
          sessions.length
        );
      } catch (err) {
        setSubmitError(
          paymentErrorMessage(err, {
            boundary: "checkout payment details",
            fallback:
              "We couldn't check your payment details. Please try again."
          })
        );
        return;
      }

      // Now trigger react-hook-form validation + onSubmit.
      await handleSubmit(onSubmit, onInvalid)();
    },
    [
      bankTransferUnpayable,
      selectedProviderId,
      sessions,
      handleSubmit,
      onSubmit,
      onInvalid
    ]
  );

  // One currency for the whole sum, not one per cart. The marketplace is
  // single-currency, and every cart in a checkout inherits the buyer's region
  // regardless - so there is one right answer here, and summing totals that
  // disagreed about currency could not produce a chargeable figure anyway.
  const stripeCurrency = currencyCode;
  // Sum each cart's total in minor units so the conversion is currency-correct
  // (zero-decimal currencies aren't *100'd). toMinorUnits owns that rule.
  const stripeAmountMinor = carts.reduce(
    (sum, c) => sum + toMinorUnits(c.totals.total, stripeCurrency),
    0
  );

  // Grand total for the mobile action bar (major units, from the carts in hand).
  const grandTotal = carts.reduce((sum, c) => sum + (c.totals?.total ?? 0), 0);

  // KAN-105 AC4: this step is where payment sessions get created, and once a
  // cart's prices are locked the marker has to survive alongside them. It does
  // because the API persists provenance next to each locked value rather than
  // deriving it from a live call - so this reads the same field it would have
  // read before the lock, and nothing here needs to know about the lock.
  const grandTotalProvenance = collapseProvenance(
    carts.map((c) => c.totals?.provenance)
  );

  // Shared between the marketplace and supplier-collected payment bodies: both
  // initiate a session and both must show the same "setting up" spinner and the
  // same retry affordance on failure. Defined once so the two branches cannot
  // drift.
  const initializingIndicator = isInitializing ? (
    <div className="flex items-center justify-center py-6">
      <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
      <span className="ml-2 text-sm text-gray-500">
        Setting up secure payment...
      </span>
    </div>
  ) : null;

  // The retry is the only affordance that can re-run initialisation for the
  // provider already selected - see CLAUDE.md, "A payment session belongs to the
  // provider that initiated it". `!isInitializing` guards an invariant the hook
  // already holds; kept as depth, not reachable today.
  const initErrorAlert =
    initError && !isInitializing ? (
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        role="alert"
      >
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{initError}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={startInitialization}
          className="ml-auto border-red-300 bg-white text-red-700 hover:bg-red-100"
        >
          Try again
        </Button>
      </div>
    ) : null;

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {/* Error Alert */}
        {submitError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{submitError}</span>
          </div>
        )}

        {deliveryAlert && (
          <DeliveryIssueAlert message={deliveryAlert} cartIds={cartIds} />
        )}

        {/* Payment Method Section */}
        <section
          className="rounded-xl border border-gray-200 bg-white"
          aria-labelledby="payment-method-heading"
        >
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
              3
            </span>
            <h2
              id="payment-method-heading"
              className="text-lg font-semibold text-gray-900"
            >
              Payment
            </h2>
          </div>
          <div className="p-6">
            {isSupplierCollected ? (
              /* Supplier-collected: no provider choice, no card form. The
                 dedicated provider is forced and initiated behind the scenes;
                 all the buyer sees is that the supplier will bill them. */
              <div className="space-y-4">
                {initializingIndicator}
                {initErrorAlert}
                {sessions.length > 0 && (
                  <SupplierCollectedInstructions
                    sellerNames={supplierCollectedSellerNames}
                  />
                )}
              </div>
            ) : (
              <>
                {/* Provider loading state */}
                {isLoadingProviders && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    <span className="ml-2 text-sm text-gray-500">
                      Loading payment methods...
                    </span>
                  </div>
                )}

                {/* Provider selection */}
                {!isLoadingProviders && paymentProviders.length > 0 && (
                  <div className="space-y-4">
                    <fieldset>
                      <legend className="sr-only">Payment method</legend>
                      <div className="space-y-2">
                        {paymentProviders.map((provider) => {
                          const info = paymentInfoMap[provider.id];
                          const isSelected = selectedProviderId === provider.id;

                          return (
                            <label
                              key={provider.id}
                              className={cn(
                                "flex items-center gap-3 rounded-lg border p-4 transition-colors",
                                // Locked while initialising - but still legibly
                                // selected, so the buyer can see what they picked.
                                isInitializing
                                  ? "cursor-not-allowed opacity-60"
                                  : "cursor-pointer",
                                isSelected
                                  ? "border-purple-600 bg-purple-50 ring-1 ring-purple-600"
                                  : "border-gray-200",
                                !isSelected &&
                                  !isInitializing &&
                                  "hover:border-gray-300 hover:bg-gray-50"
                              )}
                            >
                              <input
                                type="radio"
                                name="paymentProvider"
                                value={provider.id}
                                checked={isSelected}
                                // KAN-48: switching provider mid-initialisation is
                                // what strands a superseded run's sessions. The
                                // hook's run token is the correctness fix; this
                                // closes the ordinary double-click that reaches it,
                                // and makes the lock visible rather than silently
                                // swallowing the second click. `usePaymentSessions`
                                // holds the flag up across a provider switch so
                                // this never gaps between the two runs.
                                disabled={isInitializing}
                                onChange={() =>
                                  handleProviderChange(provider.id)
                                }
                                className="h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500 disabled:cursor-not-allowed"
                              />
                              <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                {info?.icon ?? (
                                  <CreditCard className="h-5 w-5" />
                                )}
                                {info?.title ?? provider.id}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    {initializingIndicator}

                    {initErrorAlert}

                    {/* Stripe payment form — single form, reused for all carts.
                    Must stay mounted during payment (don't gate on allReady). */}
                    {isStripe(selectedProviderId ?? undefined) &&
                      sessions.length > 0 && (
                        <div className="pt-2">
                          <StripePaymentForm
                            ref={(el) => {
                              if (el)
                                paymentFormRefs.current.set(
                                  sessions[0].cartId,
                                  el
                                );
                              else
                                paymentFormRefs.current.delete(
                                  sessions[0].cartId
                                );
                            }}
                            cartId={sessions[0].cartId}
                            amount={stripeAmountMinor}
                            currency={stripeCurrency}
                            onChange={() => setSubmitError(null)}
                          />
                        </div>
                      )}

                    {/* Bank transfer instructions + copyable payee details.
                    Gated on sessions.length rather than allReady, like the
                    Stripe form above: the panel must stay mounted while the
                    carts complete, or a partial failure leaves the buyer with
                    orders created and no account to pay into. `sessions` is
                    only set once every session has initiated, so there is no
                    window where this renders ahead of the details. */}
                    {isBankTransfer(selectedProviderId ?? undefined) &&
                      sessions.length > 0 && (
                        <BankTransferInstructions
                          details={bankTransferDetails}
                          orderCount={sessions.length}
                        />
                      )}

                    {/* Manual payment message */}
                    {isManual(selectedProviderId ?? undefined) && allReady && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        Payment will be collected upon delivery or as arranged
                        with the seller.
                      </div>
                    )}
                  </div>
                )}

                {/* No providers available */}
                {!isLoadingProviders &&
                  paymentProviders.length === 0 &&
                  regionId && (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>No payment methods available for your region.</span>
                    </div>
                  )}
              </>
            )}
          </div>
        </section>

        {/* Billing Address Section */}
        <section
          className="rounded-xl border border-gray-200 bg-white"
          aria-labelledby="billing-address-heading"
        >
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
              4
            </span>
            <h2
              id="billing-address-heading"
              className="text-lg font-semibold text-gray-900"
            >
              Billing Address
            </h2>
          </div>
          <div className="p-6">
            <BillingAddressSection
              form={form}
              addresses={addresses}
              isLoading={isLoading}
              disabled={isPending}
              onAddressChange={handleBillingAddressChange}
            />
          </div>
        </section>

        {/* Pay action - under the total in the order summary from lg (inline
            here if the summary is unavailable), in a sticky bottom bar below
            it (KAN-77). The Pay button, its synchronous click handler and its
            disabled state are exactly as before; the bar only repositions it
            and defers nothing. */}
        <CheckoutActionBar
          total={grandTotal}
          provenance={grandTotalProvenance}
          secondary={
            <Link
              to="/cart"
              className="inline-flex items-center gap-1 rounded text-sm font-medium text-purple-600 hover:text-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Cart
            </Link>
          }
          primary={
            <Button
              type="button"
              size="lg"
              disabled={!canSubmit}
              onClick={handlePayClick}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 focus-visible:ring-purple-500"
            >
              {isPending ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  Processing...
                </>
              ) : (
                // Supplier-collected takes no payment here, so "Pay now" would
                // misdescribe the action - the salon is placing an order the
                // supplier will bill for.
                <>{isSupplierCollected ? "Place order" : "Pay now"}</>
              )}
            </Button>
          }
        />
      </form>
    </Form>
  );
});
